import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { LOADING, NOT_FOUND } from "abreact/tree/AbTreeListItem";
import { AbTreeUtils } from "abreact/tree/AbTreeUtils";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AB_ITEM_SEPARATOR } from "abweb/collection/AbItemSeparator";
import { AbKeyMap } from "abweb/collection/AbKeyMap";
import { ArrayUtils } from "abweb/utils/ArrayUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import { AbAsyncUtils } from "abweb/utils/async/AbAsyncUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
const LOGGER = LogUtils.getLogger(__filename);
export class AbTreeChildNodeImpl {
  constructor(_data, _parent, _root) {
    _defineProperty(this, "_expanded", false);
    _defineProperty(this, "_size", 1);
    // number of rows required when displayed.
    _defineProperty(this, "_depth", 0);
    _defineProperty(this, "_children", undefined);
    _defineProperty(this, "_childrenPromise", undefined);
    _defineProperty(this, "_filteredChildren", undefined);
    _defineProperty(this, "_hasFilterMatchAncestor", undefined);
    _defineProperty(this, "_path", []);
    _defineProperty(this, "_enrichedPath", []);
    _defineProperty(this, "invalidateFilter", () => {
      this._size = undefined;
      this._filteredChildren = undefined;
      this._hasFilterMatchAncestor = undefined;
      this.cachedChildren.forEach(child => child !== AB_ITEM_SEPARATOR && child.invalidateFilter());
    });
    _defineProperty(this, "invalidateSize", () => {
      if (this._size === undefined) return;
      this._size = undefined;
      this._parent.invalidateSize();
    });
    _defineProperty(this, "itemAt", index => {
      if (index === 0) return this.value === AB_ITEM_SEPARATOR ? AB_ITEM_SEPARATOR : {
        path: this.path,
        enrichedPath: this.enrichedPath,
        expander: this.expander,
        enrichment: this.enrichment
      };
      if (index === 1 && this._children === LOADING) {
        return {
          value: LOADING,
          depth: this.depth + 1
        };
      }
      let remainingSize = index - 1;
      for (const child of this.children) {
        const childSize = child === AB_ITEM_SEPARATOR ? 1 : child.size;
        if (remainingSize < childSize) {
          if (child === AB_ITEM_SEPARATOR) return child;
          return child.itemAt(remainingSize);
        }
        remainingSize -= childSize;
      }
      LOGGER.error(`Invalid tree index c2 ${index}, ${this.value}`);
      return {
        value: NOT_FOUND,
        depth: this.depth + 1
      };
    });
    _defineProperty(this, "expand", () => {
      if (isChildrenEmpty(this._data.children)) return undefined; // stop if already expanded or if no children
      if (this._expanded) return this._childrenPromise;
      this._expanded = true;
      if (this._children) {
        this.setSize();
        this._root.onExpand?.(this.path);
        return undefined;
      }
      this._children = LOADING;
      this.setSize();
      const promise = this.refresh(this._data);
      this._root.onExpand?.(this.path, promise);
      return promise;
    });
    _defineProperty(this, "collapse", () => {
      this._expanded = false;
      this.setSize();
      this._root.onCollapse?.(this.path);
    });
    _defineProperty(this, "cacheChildren", () => {
      if (this._childrenPromise && this._children === LOADING) return this._childrenPromise;
      if (this.enrichment?.disableCaching || this._expanded || isChildrenEmpty(this._data.children) || this._children) return undefined; // stop if already expanded or if no children or children already cached
      this._children = LOADING;
      return this.refresh(this._data);
    });
    _defineProperty(this, "refresh", data => {
      if (data) this._data = data; // update data cache
      this.cachePaths();
      if (!this._children) return; // If children haven't already been calculated, do nothing

      if (AbAsyncUtils.isAsyncFunc(this._data.children)) {
        const childrenPromise = this._data.children().then(children => {
          if (childrenPromise === this._childrenPromise) {
            this.setChildren(children);
            this._childrenPromise = undefined;
          }
        });
        this._childrenPromise = childrenPromise;
      } else {
        this.setChildren(this._data.children);
        this._childrenPromise = undefined;
      }
      return this._childrenPromise;
    });
    _defineProperty(this, "cachePaths", () => {
      this._path = [...this._parent.path, this.value];
      this._enrichedPath = [...this._parent.enrichedPath, this._data.value];
    });
    _defineProperty(this, "setChildren", newChildren => {
      const treeDataKeyFunction = root => TypeUtils.itemToKey(AbEnriched.getItem(root.value), this.keyFunction);
      const prevChildren = AbKeyMap.from((this._children === LOADING || this._children === undefined ? [] : this._children).filter(child => child !== AB_ITEM_SEPARATOR).map(root => [treeDataKeyFunction(root), root]));
      if (newChildren === undefined) {
        this._children = undefined;
      } else {
        this._children = newChildren.map(child => {
          if (child === AB_ITEM_SEPARATOR) return child;
          const key = treeDataKeyFunction(child);
          const oldChild = prevChildren.get(key);
          if (oldChild !== AbKeyMap.NOT_FOUND) {
            oldChild.refresh(child);
            return oldChild;
          }
          return new AbTreeChildNodeImpl(child, this, this._root);
        });
      }
      this._filteredChildren = undefined;
      this.setSize();
    });
    _defineProperty(this, "setSize", () => {
      this._size = this.calcSize();
      this._parent.invalidateSize();
    });
    _defineProperty(this, "calcSize", () => {
      if (!this._expanded || this._children === undefined) return 1;
      if (this._children === LOADING) return 2;
      return this.children.reduce((acc, child) => acc + (child === AB_ITEM_SEPARATOR ? 1 : child.size), 1);
    });
    _defineProperty(this, "find", path => AbTreeUtils.find(this, path));
    _defineProperty(this, "expandPath", path => AbTreeUtils.expandPath(this, path));
    _defineProperty(this, "expandPaths", paths => AbTreeUtils.expandPaths(this, paths));
    _defineProperty(this, "expandAll", () => AbTreeUtils.expandAll(this));
    _defineProperty(this, "collapseAll", () => AbTreeUtils.collapseAll(this));
    _defineProperty(this, "collapsePath", path => AbTreeUtils.collapsePath(this, path));
    _defineProperty(this, "toggle", path => AbTreeUtils.toggle(this, path));
    _defineProperty(this, "getExpandedPaths", () => AbTreeUtils.getExpandedPaths(this));
    _defineProperty(this, "hasAsyncChildren", () => this._data.children !== undefined && !ArrayUtils.isArrayLike(this._data.children));
    this._data = _data;
    this._parent = _parent;
    this._root = _root;
    this._depth = this._parent.depth + 1;
    if (AbEnriched.getEnrichment(_data.value)?.defaultExpanded) this.expand();
    this.cachePaths();
  }
  get value() {
    return AbEnriched.getItem(this._data.value);
  }
  get enrichment() {
    return AbEnriched.getEnrichment(this._data.value);
  }
  get depth() {
    return this._depth;
  }
  get size() {
    if (this._size === undefined) {
      this._size = this.calcSize();
    }
    return this._size;
  }
  get keyFunction() {
    return this._root.keyFunction;
  }
  get parent() {
    return this._parent;
  }
  get hasMatchAncestor() {
    if (!this._root._filterSets) return true;
    if (this._hasFilterMatchAncestor === undefined) {
      this._hasFilterMatchAncestor = this._parent.hasMatchAncestor || this._root._filterSets.matchedNodes.has(this);
    }
    return this._hasFilterMatchAncestor;
  }
  calcChildren(allChildren) {
    if (this._root._filterSets) {
      if (!this._filteredChildren) {
        const filterSets = this._root._filterSets;
        this._filteredChildren = allChildren.filter(child => child !== AB_ITEM_SEPARATOR && (filterSets.expandedNodes.has(child) || filterSets.matchedNodes.has(child) || child.hasMatchAncestor));
      }
      return this._filteredChildren;
    }
    return allChildren;
  }
  get children() {
    if (this._children === LOADING || this.value === AB_ITEM_SEPARATOR || this._children === undefined || !this._expanded) return [];
    return this.calcChildren(this._children);
  }
  get cachedChildren() {
    return this._children === LOADING || this._children === undefined ? [] : this._children;
  }
  get path() {
    if (this.value === AB_ITEM_SEPARATOR) return [];
    return this._path;
  }
  get enrichedPath() {
    if (this._data.value === AB_ITEM_SEPARATOR) return [];
    return this._enrichedPath;
  }
  get expander() {
    if (this._children === undefined) {
      if (isChildrenEmpty(this._data.children)) return undefined;
      return {
        expand: this.expand,
        collapse: this.collapse,
        isExpanded: () => this._expanded
      };
    }
    if (this._children !== LOADING && this.calcChildren(this._children).length === 0) return undefined;
    return {
      expand: this.expand,
      collapse: this.collapse,
      isExpanded: () => this._expanded
    };
  }
}
const isChildrenEmpty = children => {
  return !children || ArrayUtils.isArrayLike(children) && children.length === 0;
};
//# sourceMappingURL=AbTreeChildNodeImpl.js.map
